import React, { useState, useEffect } from 'react';
import { FolderPlus, Plus, Edit3, Trash2, X, Tag, FileText, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

// Priority scale: 1 = highest priority, 10 = lowest priority
// Lower numbers override higher numbers when there's conflicting information

const getPriorityColor = (priority) => {
  if (priority <= 2) return 'bg-red-100 text-red-700';
  if (priority <= 4) return 'bg-orange-100 text-orange-700';
  if (priority <= 6) return 'bg-amber-100 text-amber-700';
  if (priority <= 8) return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
};

const getPriorityLabel = (priority) => {
  if (priority <= 2) return `Priority ${priority} (Critical)`;
  if (priority <= 4) return `Priority ${priority} (High)`;
  if (priority <= 6) return `Priority ${priority} (Standard)`;
  if (priority <= 8) return `Priority ${priority} (Low)`;
  return `Priority ${priority} (Baseline)`;
};

// Helper to get priority info object (replacing old PRIORITY_LEVELS system)
const getPriorityInfo = (priority) => {
  return {
    label: getPriorityLabel(priority),
    color: getPriorityColor(priority)
  };
};

const tagTypeOptions = [
  { type: 'blood_type', label: 'Blood Type' },
  { type: 'genotype', label: 'Genotype' },
  { type: 'sex', label: 'Sex' },
  { type: 'health_goal', label: 'Health Goal' },
  { type: 'health_issue', label: 'Health Issue' },
  { type: 'pregnancy_stage', label: 'Pregnancy Stage' },
  { type: 'postpartum', label: 'Postpartum' },
  { type: 'trying_to_conceive', label: 'Trying to Conceive' },
];

const bloodTypeValues = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const sexValues = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];
const pregnancyStageValues = [
  { value: 'trimester_1', label: 'First Trimester' },
  { value: 'trimester_2', label: 'Second Trimester' },
  { value: 'trimester_3', label: 'Third Trimester' },
];

export default function AdminDocumentFoldersPage() {
  const [folders, setFolders] = useState([]);
  const [unorganizedCount, setUnorganizedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [folderDocuments, setFolderDocuments] = useState({});
  
  // Dynamic options from backend
  const [genotypes, setGenotypes] = useState([]);
  const [healthGoals, setHealthGoals] = useState([]);
  const [healthIssues, setHealthIssues] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: [],
    priority: 5,  // Default to middle priority (1-10 scale)
  });
  
  const [newTag, setNewTag] = useState({ type: '', value: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [foldersRes, genotypesRes, goalsRes, issuesRes] = await Promise.all([
        adminAPI.getFolders(),
        adminAPI.getGenotypes().catch(() => ({ data: { genotypes: [] } })),
        adminAPI.getHealthGoals().catch(() => ({ data: { goals: [] } })),
        adminAPI.getHealthIssues().catch(() => ({ data: { issues: [] } })),
      ]);
      
      setFolders(foldersRes.data.folders || []);
      setUnorganizedCount(foldersRes.data.unorganized_count || 0);
      setGenotypes(genotypesRes.data.genotypes || []);
      setHealthGoals(goalsRes.data.goals || []);
      setHealthIssues(issuesRes.data.issues || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const loadFolderDocuments = async (folderId) => {
    if (folderDocuments[folderId]) return;
    try {
      const response = await adminAPI.getFolderDocuments(folderId);
      setFolderDocuments(prev => ({ ...prev, [folderId]: response.data.documents || [] }));
    } catch (error) {
      console.error('Failed to load folder documents:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      tags: [],
      priority: 5,
    });
    setNewTag({ type: '', value: '' });
  };

  const addTag = () => {
    if (!newTag.type || !newTag.value) {
      toast.error('Please select both tag type and value');
      return;
    }
    
    // Check for duplicate
    const exists = formData.tags.some(t => t.type === newTag.type && t.value === newTag.value);
    if (exists) {
      toast.error('This tag already exists');
      return;
    }
    
    const updatedTags = [...formData.tags, { ...newTag }];
    
    setFormData(prev => ({
      ...prev,
      tags: updatedTags,
    }));
    setNewTag({ type: '', value: '' });
  };

  const removeTag = (index) => {
    const updatedTags = formData.tags.filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      tags: updatedTags,
    }));
  };

  const getTagValueOptions = (tagType) => {
    switch (tagType) {
      case 'blood_type':
        return bloodTypeValues.map(v => ({ value: v, label: v }));
      case 'genotype':
        return genotypes.map(g => ({ value: g.name, label: g.name }));
      case 'sex':
        return sexValues;
      case 'health_goal':
        return healthGoals.map(g => ({ value: g.id, label: g.label }));
      case 'health_issue':
        return healthIssues.map(i => ({ value: i.id, label: i.label }));
      case 'pregnancy_stage':
        return pregnancyStageValues;
      case 'postpartum':
        return [{ value: 'yes', label: 'Yes (within last 12 months)' }];
      case 'trying_to_conceive':
        return [{ value: 'yes', label: 'Yes' }];
      default:
        return [];
    }
  };

  const handleAddFolder = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a folder name');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.createFolder({
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        priority: formData.priority,
      });
      toast.success('Folder created');
      setShowAddDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a folder name');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.updateFolder(editingFolder.id, {
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        priority: formData.priority,
        overrides_lower_priority: formData.overrides_lower_priority,
      });
      toast.success('Folder updated');
      setEditingFolder(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await adminAPI.deleteFolder(folderId);
      toast.success('Folder deleted. Documents moved to unorganized.');
      loadData();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const startEdit = (folder) => {
    setFormData({
      name: folder.name,
      description: folder.description || '',
      tags: folder.tags || [],
      priority: folder.priority || 5, // Default to middle priority
    });
    setEditingFolder(folder);
    setShowAddDialog(true);
  };

  const toggleFolderExpand = (folderId) => {
    if (expandedFolder === folderId) {
      setExpandedFolder(null);
    } else {
      setExpandedFolder(folderId);
      loadFolderDocuments(folderId);
    }
  };

  // Sort folders by priority (lower number = higher priority, so sort ascending)
  const sortedFolders = [...folders].sort((a, b) => (a.priority || 5) - (b.priority || 5));

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Document Folders</h1>
            <p className="text-[#4A2C2A]/60 mt-1">
              Organize documents by user attributes. Higher priority folders override lower priority advice.
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditingFolder(null); resetForm(); } }}>
            <Button 
              onClick={() => { resetForm(); setEditingFolder(null); setShowAddDialog(true); }}
              className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82]"
              data-testid="add-folder-btn"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> New Folder
            </Button>
            <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#4A2C2A]">
                  {editingFolder ? 'Edit Folder' : 'Create Document Folder'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={editingFolder ? handleUpdateFolder : handleAddFolder} className="space-y-5 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">Folder Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Blood Type A+ Diet"
                    className="input-warm"
                    data-testid="folder-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What type of content does this folder contain?"
                    className="input-warm"
                    rows={2}
                    data-testid="folder-description-input"
                  />
                </div>

                {/* Tags Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#4A2C2A]">Target Audience Tags</Label>
                    <span className="text-xs text-[#4A2C2A]/50">Documents shown to users matching ANY tag</span>
                  </div>
                  
                  {/* Current Tags */}
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-[#FDFBF7] rounded-lg">
                      {formData.tags.map((tag, index) => {
                        const tagOption = tagTypeOptions.find(t => t.type === tag.type);
                        return (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-[#8DA399]/20 text-[#4A2C2A]"
                          >
                            <Tag className="w-3 h-3" />
                            <span className="font-medium">{tagOption?.label || tag.type}:</span>
                            <span>{tag.value}</span>
                            <button
                              type="button"
                              onClick={() => removeTag(index)}
                              className="ml-1 hover:opacity-70"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Add Tag Form */}
                  <div className="flex gap-2">
                    <Select
                      value={newTag.type}
                      onValueChange={(value) => setNewTag({ type: value, value: '' })}
                    >
                      <SelectTrigger className="input-warm flex-1" data-testid="tag-type-select">
                        <SelectValue placeholder="Select tag type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tagTypeOptions.map(opt => (
                          <SelectItem key={opt.type} value={opt.type}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={newTag.value}
                      onValueChange={(value) => setNewTag(prev => ({ ...prev, value }))}
                      disabled={!newTag.type}
                    >
                      <SelectTrigger className="input-warm flex-1" data-testid="tag-value-select">
                        <SelectValue placeholder="Select value..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getTagValueOptions(newTag.type).map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Button
                      type="button"
                      onClick={addTag}
                      disabled={!newTag.type || !newTag.value}
                      className="bg-[#8DA399] text-white hover:bg-[#7A9186]"
                      data-testid="add-tag-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Priority Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#4A2C2A]">Priority Level (1-10)</Label>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(formData.priority)}`}>
                      {getPriorityLabel(formData.priority)}
                    </span>
                  </div>
                  <Select
                    value={String(formData.priority)}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: parseInt(value) }))}
                  >
                    <SelectTrigger className="input-warm" data-testid="priority-select">
                      <SelectValue placeholder="Select priority..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                        <SelectItem key={num} value={String(num)}>
                          {num} - {num <= 2 ? 'Critical' : num <= 4 ? 'High' : num <= 6 ? 'Standard' : num <= 8 ? 'Low' : 'Baseline'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#4A2C2A]/60">
                    Lower numbers = higher priority. Priority 1 documents override all others.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); setEditingFolder(null); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                    data-testid="save-folder-btn"
                  >
                    {saving ? 'Saving...' : editingFolder ? 'Update Folder' : 'Create Folder'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Priority Hierarchy Info */}
        <div className="bg-gradient-to-r from-[#E8A095]/10 to-[#8DA399]/10 border border-[#E8A095]/20 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#4A2C2A] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#4A2C2A]">Priority Hierarchy System (1-10)</p>
              <p className="text-sm text-[#4A2C2A]/70 mt-1 mb-3">
                When a user has multiple attributes (e.g., Blood Type A+ AND Cancer), lower priority numbers override higher numbers.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">1-2: Critical</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">3-4: High</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">5-6: Standard</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">7-8: Low</span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">9-10: Baseline</span>
              </div>
              <p className="text-xs text-[#4A2C2A]/50 mt-2">
                Example: If user has Cancer (priority 1) and Blood Type A+ (priority 7), cancer advice takes precedence.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Unorganized Documents Banner */}
            {unorganizedCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Unorganized Documents</p>
                    <p className="text-sm text-amber-600">{unorganizedCount} document(s) not assigned to any folder</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => window.location.href = '/admin/documents'}
                >
                  Manage Documents
                </Button>
              </div>
            )}

            {/* Folders List */}
            {sortedFolders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E8A095]/20">
                <FolderPlus className="w-12 h-12 mx-auto text-[#4A2C2A]/30 mb-4" />
                <h3 className="font-medium text-[#4A2C2A]">No Folders Yet</h3>
                <p className="text-[#4A2C2A]/60 mt-1">Create folders to organize documents by user attributes.</p>
              </div>
            ) : (
              sortedFolders.map((folder) => {
                const priorityInfo = getPriorityInfo(folder.priority || 5);
                const isExpanded = expandedFolder === folder.id;
                const docs = folderDocuments[folder.id] || [];
                
                return (
                  <div key={folder.id} className="bg-white rounded-xl border border-[#E8A095]/20 overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => toggleFolderExpand(folder.id)}
                            className="p-1 hover:bg-[#FDFBF7] rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-[#4A2C2A]/50" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-[#4A2C2A]/50" />
                            )}
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-[#4A2C2A]">{folder.name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityInfo.color}`}>
                                {priorityInfo.label}
                              </span>
                              <span className="text-xs text-[#4A2C2A]/40">
                                {folder.document_count || 0} doc(s)
                              </span>
                            </div>
                            {folder.description && (
                              <p className="text-sm text-[#4A2C2A]/60 mt-0.5">{folder.description}</p>
                            )}
                            {folder.tags && folder.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {folder.tags.map((tag, idx) => {
                                  const tagOption = tagTypeOptions.find(t => t.type === tag.type);
                                  return (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FDFBF7] border border-[#E8A095]/10 rounded-full text-xs text-[#4A2C2A]/70"
                                    >
                                      <Tag className="w-2.5 h-2.5" />
                                      {tagOption?.label || tag.type}: {tag.value}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(folder)}
                            className="text-[#4A2C2A]/60 hover:text-[#4A2C2A]"
                            data-testid={`edit-folder-${folder.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500/60 hover:text-red-500"
                                data-testid={`delete-folder-${folder.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Folder</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{folder.name}"? 
                                  {folder.document_count > 0 && (
                                    <span className="block mt-2 text-amber-600">
                                      {folder.document_count} document(s) will be moved to unorganized.
                                    </span>
                                  )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteFolder(folder.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Documents List */}
                    {isExpanded && (
                      <div className="border-t border-[#E8A095]/10 bg-[#FDFBF7] p-4">
                        {docs.length === 0 ? (
                          <p className="text-sm text-[#4A2C2A]/50 text-center py-2">
                            No documents in this folder yet
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {docs.map(doc => (
                              <div key={doc.id} className="flex items-center gap-2 text-sm">
                                <FileText className="w-4 h-4 text-[#4A2C2A]/40" />
                                <span className="text-[#4A2C2A]">{doc.title}</span>
                                <span className="text-[#4A2C2A]/40">({doc.category})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
